import React, { useState, useEffect, FormEvent, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { publicApi, clusterApi, ApiError } from "../services/api";
import SearchIcon from "@mui/icons-material/Search";
import LockIcon from "@mui/icons-material/Lock";
import EmailIcon from "@mui/icons-material/Email";
import WarningIcon from "@mui/icons-material/Warning";
import BusinessIcon from "@mui/icons-material/Business";
import "./SetPasswordPage.css";

const SetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [invalidTokenError, setInvalidTokenError] = useState("");
  const [email, setEmail] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const token = searchParams.get("token");

  const navigateToFirstClient = useCallback(async () => {
    try {
      const clients = await clusterApi.getClients();
      if (clients && clients.length > 0) {
        navigate(`/${clients[0].id}`);
      } else {
        navigate("/default");
      }
    } catch {
      navigate("/default");
    }
  }, [navigate]);

  useEffect(() => {
    if (!token) {
      setInvalidTokenError("Missing invitation token. Please use the link from your invite email.");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const validate = async () => {
      try {
        const data = await publicApi.validateInviteToken(token);
        if (!cancelled) {
          setEmail(data.email);
          setTenantName(data.tenant_name);
          setInvalidTokenError("");
        }
      } catch (err) {
        if (!cancelled) {
          setInvalidTokenError(
            err instanceof ApiError ? err.message : "Invalid or expired invitation"
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    validate();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError("");

    if (password !== confirmPassword) {
      setFormError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setFormError("Password must be at least 8 characters");
      return;
    }

    if (!token) return;

    setIsSubmitting(true);

    try {
      const { access_token } = await publicApi.acceptInvite(token, password);
      loginWithToken(access_token);
      setSuccess(true);
      await navigateToFirstClient();
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message : "Failed to accept invitation"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="set-password-page">
        <div className="set-password-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>
        <div className="set-password-container">
          <div className="set-password-card">
            <div className="set-password-loading">
              <span className="button-spinner"></span>
              <p>Validating invitation...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (invalidTokenError) {
    return (
      <div className="set-password-page">
        <div className="set-password-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>
        <div className="set-password-container">
          <div className="set-password-card">
            <div className="set-password-header">
              <div className="set-password-logo">
                <SearchIcon className="logo-icon" sx={{ fontSize: 32 }} />
              </div>
              <h1>Invalid Invitation</h1>
              <p className="set-password-subtitle">
                This invitation link is invalid or has expired.
              </p>
            </div>
            <div className="set-password-error">
              <WarningIcon className="error-icon" fontSize="small" />
              {invalidTokenError}
            </div>
            <div className="set-password-footer">
              <p className="set-password-signup-link">
                <Link to="/login">Back to Sign In</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="set-password-page">
      <div className="set-password-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <div className="set-password-container">
        <div className="set-password-card">
          <div className="set-password-header">
            <div className="set-password-logo">
              <LockIcon className="logo-icon" sx={{ fontSize: 32 }} />
            </div>
            <h1>Set Your Password</h1>
            <p className="set-password-subtitle">
              You've been invited to join {tenantName}. Create a password to get started.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="set-password-form">
            {formError && (
              <div className="set-password-error">
                <WarningIcon className="error-icon" fontSize="small" />
                {formError}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <div className="input-wrapper">
                <EmailIcon className="input-icon" fontSize="small" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  readOnly
                  disabled
                  className="input-readonly"
                />
              </div>
            </div>

            {tenantName && (
              <div className="form-group">
                <label htmlFor="tenant">Organization</label>
                <div className="input-wrapper">
                  <BusinessIcon className="input-icon" fontSize="small" />
                  <input
                    id="tenant"
                    type="text"
                    value={tenantName}
                    readOnly
                    disabled
                    className="input-readonly"
                  />
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <LockIcon className="input-icon" fontSize="small" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="input-wrapper">
                <LockIcon className="input-icon" fontSize="small" />
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <button
              type="submit"
              className="set-password-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="button-spinner"></span>
                  Setting password...
                </>
              ) : success ? (
                "Redirecting..."
              ) : (
                "Accept Invitation"
              )}
            </button>
          </form>

          <div className="set-password-footer">
            <p className="set-password-signup-link">
              Already have an account? <Link to="/login">Sign In</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetPasswordPage;
